import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { isNativeApp } from "@/utils/platform";
import { GOOGLE_STATE_KEY } from "@/utils/googleOauth";

/**
 * App native uniquement : écoute les deep links de retour d'authentification.
 * - neldaari://auth/google?code=…&state=…  → nouveau flux OAuth (client NelDaari),
 *   code échangé côté backend via POST /auth/google/session.
 * - neldaari://auth/callback#session_id=… → legacy Emergent (transition).
 * Le login Google s'ouvre dans le navigateur INTÉGRÉ (Guideline 4) ; le retour
 * passe par la page web /auth/google qui relaie vers ces deep links.
 */
export default function NativeAuthListener() {
  const navigate = useNavigate();
  const { setUser } = useAuth();

  useEffect(() => {
    if (!isNativeApp) return undefined;
    let handle;
    (async () => {
      const { App } = await import("@capacitor/app");
      handle = await App.addListener("appUrlOpen", async ({ url }) => {
        const codeM = /[?&]code=([^&]+)/.exec(url || "");
        const stateM = /[?&]state=([^&]+)/.exec(url || "");
        const sessM = /session_id=([^&]+)/.exec(url || "");
        const isGoogle = url?.includes("auth/google") && codeM;
        const isLegacy = url?.includes("auth/callback") && sessM;
        if (!isGoogle && !isLegacy) return;
        try {
          const { Browser } = await import("@capacitor/browser");
          await Browser.close();
        } catch { /* Browser peut être déjà fermé */ }
        try {
          let data;
          if (isGoogle) {
            // Anti-CSRF : le state doit correspondre à celui émis au départ
            const expected = localStorage.getItem(GOOGLE_STATE_KEY);
            const state = decodeURIComponent(stateM?.[1] || "");
            if (!expected || state !== expected) { navigate("/login"); return; }
            localStorage.removeItem(GOOGLE_STATE_KEY);
            ({ data } = await apiClient.post("/auth/google/session", {
              code: decodeURIComponent(codeM[1]),
              redirect_uri: process.env.REACT_APP_BACKEND_URL + "/auth/google",
            }));
          } else {
            ({ data } = await apiClient.post("/auth/google/session", { session_id: sessM[1] }));
          }
          setUser(data);
          navigate("/", { replace: true });
        } catch {
          navigate("/login");
        }
      });
    })();
    return () => { handle?.remove?.(); };
  }, [navigate, setUser]);

  return null;
}
